import type { CSSProperties, ReactNode } from 'react'

// ── Design tokens ───────────────────────────────────────────────────────────

export const tokens = {
  pageBg: 'var(--app-page-bg)',
  cardBg: 'var(--app-card-bg)',
  text: 'var(--tg-theme-text-color)',
  accent: 'var(--tg-theme-button-color)',
  accentText: 'var(--tg-theme-button-text-color)',
  link: 'var(--tg-theme-link-color)',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
} as const

export const styles = {
  text: { color: tokens.text } as CSSProperties,
  subtle: { color: tokens.text, opacity: 0.55 } as CSSProperties,
  faint: { color: tokens.text, opacity: 0.35 } as CSSProperties,
  card: {
    background: tokens.cardBg,
    borderRadius: 16,
    padding: 18,
  } as CSSProperties,
  innerTile: {
    background: tokens.pageBg,
    borderRadius: 12,
    padding: 12,
  } as CSSProperties,
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: tokens.text,
    opacity: 0.45,
    marginBottom: 10,
  } as CSSProperties,
}

// ── Card ─────────────────────────────────────────────────────────────────────

export function Card({
  children,
  title,
  action,
  padded = true,
  style,
}: {
  children: ReactNode
  title?: ReactNode
  action?: ReactNode
  padded?: boolean
  style?: CSSProperties
}) {
  return (
    <div
      style={{
        background: tokens.cardBg,
        borderRadius: 18,
        padding: padded ? 18 : 0,
        ...style,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: padded ? 14 : 0,
            padding: padded ? 0 : '14px 18px 0',
          }}
        >
          {title && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: tokens.text,
                letterSpacing: '-0.005em',
              }}
            >
              {title}
            </div>
          )}
          {action}
        </div>
      )}
      <div style={padded ? undefined : { padding: '0 18px 18px' }}>{children}</div>
    </div>
  )
}

// ── Avatar with initials & deterministic color ──────────────────────────────

const AVATAR_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
]

function hashIdx(seed: string, mod: number): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h) % mod
}

export function Avatar({
  src,
  name,
  size = 40,
  emoji,
}: {
  src?: string | null
  name?: string | null
  size?: number
  emoji?: string
}) {
  const initials = (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || (emoji ? '' : '?')

  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: size,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    )
  }

  const bg = name
    ? AVATAR_PALETTE[hashIdx(name, AVATAR_PALETTE.length)]
    : 'var(--app-page-bg)'

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        background: bg,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(11, Math.round(size * 0.38)),
        fontWeight: 700,
        letterSpacing: '0.02em',
        flexShrink: 0,
      }}
    >
      {emoji || initials}
    </div>
  )
}

// ── Badge / chip ────────────────────────────────────────────────────────────

export type BadgeTone = 'default' | 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'teal'

const TONE_BG: Record<BadgeTone, string> = {
  default: 'var(--app-page-bg)',
  green: '#22c55e22',
  amber: '#f59e0b22',
  red: '#ef444422',
  blue: '#3b82f622',
  purple: '#8b5cf622',
  teal: '#14b8a622',
}
const TONE_FG: Record<BadgeTone, string> = {
  default: 'var(--tg-theme-text-color)',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  teal: '#14b8a6',
}

export function Badge({
  children,
  tone = 'default',
  icon,
}: {
  children: ReactNode
  tone?: BadgeTone
  icon?: ReactNode
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: TONE_BG[tone],
        color: TONE_FG[tone],
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 999,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {children}
    </span>
  )
}

// ── KPI ──────────────────────────────────────────────────────────────────────

export function Kpi({
  label,
  value,
  sub,
  tone,
  icon,
}: {
  label: string
  value: ReactNode
  sub?: string
  tone?: BadgeTone
  icon?: ReactNode
}) {
  const fg = tone ? TONE_FG[tone] : tokens.text
  return (
    <div style={styles.innerTile}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.02em',
          opacity: 0.55,
          color: tokens.text,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {icon}
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: fg, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ ...styles.subtle, fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ── Empty state ──────────────────────────────────────────────────────────────

export function EmptyState({
  emoji = '✨',
  title,
  subtitle,
}: {
  emoji?: string
  title: string
  subtitle?: string
}) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '48px 24px',
        color: tokens.text,
      }}
    >
      <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.7 }}>{emoji}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 12, opacity: 0.5, marginTop: 4 }}>{subtitle}</div>
      )}
    </div>
  )
}

// ── Button ───────────────────────────────────────────────────────────────────

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled,
  type = 'button',
  style,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
  size?: 'sm' | 'md'
  disabled?: boolean
  type?: 'button' | 'submit'
  style?: CSSProperties
}) {
  const sizeStyle: CSSProperties =
    size === 'sm'
      ? { padding: '6px 12px', fontSize: 12, borderRadius: 10 }
      : { padding: '10px 16px', fontSize: 13, borderRadius: 12 }

  const variantStyle: CSSProperties = {
    primary: {
      background: tokens.accent,
      color: tokens.accentText,
    },
    secondary: {
      background: tokens.pageBg,
      color: tokens.text,
    },
    ghost: {
      background: 'transparent',
      color: tokens.text,
      opacity: 0.7,
    },
    danger: { background: '#ef444422', color: '#ef4444' },
    success: { background: '#22c55e22', color: '#22c55e' },
  }[variant]

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        letterSpacing: '-0.005em',
        opacity: disabled ? 0.4 : 1,
        transition: 'opacity 0.15s',
        ...sizeStyle,
        ...variantStyle,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: tokens.cardBg,
          borderRadius: '24px 24px 0 0',
          padding: 20,
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 36,
            height: 4,
            background: tokens.pageBg,
            borderRadius: 99,
            margin: '0 auto 14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <h3 style={{ fontSize: 17, fontWeight: 700, color: tokens.text, margin: 0 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: tokens.pageBg,
              border: 'none',
              borderRadius: 99,
              width: 28,
              height: 28,
              color: tokens.text,
              opacity: 0.6,
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
        {footer && <div style={{ marginTop: 16 }}>{footer}</div>}
      </div>
    </div>
  )
}

// ── Form Field ───────────────────────────────────────────────────────────────

export function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  inputMode?: 'numeric' | 'text'
  type?: string
}) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          opacity: 0.55,
          color: tokens.text,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background: tokens.pageBg,
          color: tokens.text,
          border: 'none',
          borderRadius: 12,
          padding: '12px 14px',
          width: '100%',
          fontSize: 14,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </label>
  )
}

export function Select<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label?: string
  value: T
  onChange: (v: T) => void
  options: readonly T[] | T[]
}) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      {label && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            opacity: 0.55,
            color: tokens.text,
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          background: tokens.pageBg,
          color: tokens.text,
          border: 'none',
          borderRadius: 12,
          padding: '12px 14px',
          width: '100%',
          fontSize: 14,
          outline: 'none',
        }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )
}

// ── Search input ─────────────────────────────────────────────────────────────

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: tokens.cardBg,
        borderRadius: 14,
        padding: '0 14px',
      }}
    >
      <span style={{ opacity: 0.4, fontSize: 14 }}>🔍</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background: 'transparent',
          color: tokens.text,
          border: 'none',
          padding: '12px 0',
          width: '100%',
          fontSize: 14,
          outline: 'none',
        }}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          style={{
            background: 'transparent',
            border: 'none',
            color: tokens.text,
            opacity: 0.4,
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}

// ── Segmented pill control ───────────────────────────────────────────────────

export function PillGroup<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((opt) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '6px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: active ? tokens.accent : tokens.cardBg,
              color: active ? tokens.accentText : tokens.text,
              opacity: active ? 1 : 0.85,
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Format helpers ───────────────────────────────────────────────────────────

export function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.floor(hr / 24)
  if (days < 30) return `${days}d ago`
  const mo = Math.floor(days / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}
